/**
 * Check all properties of an object to make sure the object
 * is valid.  This is useful mostly in typeguard methods and
 * unit tests
 */
export function checkAllProperties(subject, propertyMap) {
    return propertyMap.every(([property, type, allowNull]) => {
        const outcome = checkProperty(subject, property, type, allowNull);
        if (outcome === false) {
            return false;
        }
        return true;
    });
}
/**
 * Check if a property is set and of the correct type
 *
 * Types can be primitives (for typeof) or a function (for typeguards)
 * This method is mostly useful in typeguard methotds and unit tests.
 * @see checkAllProperties
 */
export function checkProperty(subject, propertyName, propertyType, allowNull = false) {
    if (!(propertyName in subject)) {
        return false;
    }
    if (allowNull === true && subject[propertyName] === null) {
        return true;
    }
    if (typeof propertyType !== 'string') {
        return propertyType(subject[propertyName]);
    }
    if (typeof subject[propertyName] === propertyType) {
        return true;
    }
    return false;
}
/**
 * Convert a currency code to a currency symbol
 * @link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
 */
export function currencyCodeToSymbol(code) {
    const currencyPart = new Intl.NumberFormat('en', { style: 'currency', currency: code })
        .formatToParts(0)
        .find((part) => part.type === 'currency');
    return currencyPart ? currencyPart.value : '';
}
/**
 * Add a prefix to a supplied string
 */
export function addPrefix(prefix, value) {
    if (value.substring(0, prefix.length) !== prefix) {
        return prefix + value;
    }
    return value;
}
/**
 * Remove a prefix from a supplied string
 */
export function removePrefix(prefix, value) {
    const prefixLength = prefix.length;
    if (value.substring(0, prefixLength) === prefix) {
        value = value.substring(prefixLength);
    }
    return value;
}
/**
 * Attempt to parse the provided string as JSON without throwing an error.
 * Try to smooth over some common error cases.
 */
export function safeJsonParse(maybeJsonString, wantUndef = false) {
    if (typeof maybeJsonString !== 'string') {
        return undefined;
    }
    let parsed = wantUndef === false ? undefined : {};
    try {
        parsed = JSON.parse(maybeJsonString);
    }
    catch (e) {
        // Try to detect if the error was due to a newline within a quoted value;
        // if so, replace newlines with spaces and try again
        const matches = e instanceof Error ? e.message.match(/in JSON at position (\d+)/) : [];
        if (matches) {
            if (maybeJsonString[parseInt(matches[1], 10)] === '\n') {
                return safeJsonParse(maybeJsonString.replace(/\n/g, ' '), wantUndef);
            }
        }
        if (!wantUndef) {
            parsed = {};
        }
    }
    return parsed;
}
/**
 * Encode a URL for use as a query string parameter in a wrapped URL, accounting for
 * two factors:
 * 1) Because users can paste in an arbitrary URL obtained through various workflows, it
 * may already have URL-encoded characters that we don't want to double-encode. Therefore,
 * we decode before encoding.
 * 2) However, the URL we're wrapping may itself be an affiliate-wrapped URL with the
 * original merchant page URL specified in a query string. If that original URL contained
 * query string parameters that are now URL-encoded, we need to preserve that encoding.
 * To handle this, we explicitly double-encode any % characters within the query string,
 * so they will survive the subsequent decode -> encode.
 */
export function safeEncodeUrl(origUrl) {
    try {
        const parsed = new URL(origUrl);
        if (parsed.search?.includes('%')) {
            parsed.search = parsed.search.replace(/%/g, '%25');
            origUrl = parsed.toString();
        }
    }
    catch {
        console.error(`Could not parse URL ${origUrl}`);
        return origUrl;
    }
    let encodedUrl = origUrl;
    try {
        encodedUrl = encodeURIComponent(decodeURIComponent(origUrl));
    }
    catch {
        console.error(`Could not decode URL ${origUrl}`);
    }
    return encodedUrl;
}
//# sourceMappingURL=primitiveManipulation.js.map